import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TestSessionForRegistrationDto } from 'src/app/core/dtos/dto';
import { BrandEnum, TestPurposeEnum, TestTypeEnum } from 'src/app/core/dtos/enum';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { LoadingService } from 'src/app/core/services/loading.service';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { selectIsBusinessAccount } from 'src/app/state/my-account.selectors';
import { setRegTestSession } from 'src/app/state/reg-data.actions';
import { selectRegTestSession, selectTestPurposeOption } from 'src/app/state/reg-data.selectors';
import { AffiliateService } from 'src/app/core/services/affiliate.service';


@Component({
  selector: 'app-sitting-selection',
  templateUrl: './sitting-selection.component.html',
  styleUrls: ['./sitting-selection.component.scss']
})
export class SittingSelectionComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('startingModal') startingModal: ModalComponent;

  privateSittingsSubscription: Subscription;
  privateSittings: TestSessionForRegistrationDto[];
  showPrivateSittings: boolean;
  showSelectSittingsTypeOption: boolean;
  isWebDelivery: boolean;
  startingModalTitle: string;
  startingModalBody: string;
  brandId: BrandEnum;
  testTypeId: number;
  testPurposeId: number;
  caelTestTypeId = TestTypeEnum.CAEL2;
  caelTestPuposeId = TestPurposeEnum.UniversityAdmissions;
  testInfoSubscription: Subscription;
  businessAccountSubscription: Subscription;
  isBusinessAccount: boolean;
  showModal: boolean;
  affiliateId: string;
  companyName: string;
  brandName: string;

  constructor(
    private store: Store,
    private registrationService: RegistrationService,
    private configHelper: ConfigHelper,
    private toastService: HotToastService,
    private loadingService: LoadingService,
    private router: Router,
    private route: ActivatedRoute,
    private affiliateService: AffiliateService,
  ) {
  }

  ngOnInit(): void {
    this.brandId = this.configHelper.getBrandId();
    this.brandName = this.configHelper.getBrandName();
    // when using the links from WordPress
    this.route.queryParams
      .subscribe(params => {
        if (params.affiliate) {
          this.affiliateId = params.affiliate;
          this.companyName = params.companyName;
          this.affiliateService.checkAndInitAffiliateTracking();
        }

        if (this.affiliateId == "" || this.affiliateId === undefined) {
          this.affiliateService.checkAndInitAffiliateTracking();
        }

        if (this.affiliateId) {
          this.affiliateService.logoutCurrentBusinessAccount();
        }

        if (this.brandId === BrandEnum.CAEL && params.siteCode && params.testTypeId) {
          this.router.navigate(['/registration/proof-of-identity'], { queryParams: { siteCode: params.siteCode, testTypeId: params.testTypeId } });
        }
        else {
          this.showModal = true;
          this.isWebDelivery = this.router.url.includes('web-delivery');
          if (this.brandId === BrandEnum.CELPIP) {
            this.testInfoSubscription = combineLatest([
              this.store.pipe(select(selectTestPurposeOption)),
              this.store.pipe(select(selectRegTestSession))
            ]).pipe(take(1)).subscribe(
              (result) => {
                this.testPurposeId = result[0]?.testPurposeId;
                this.testTypeId = result[1]?.testTypeId;
              });
          }
          else {
            this.testTypeId = this.caelTestTypeId;
            this.testPurposeId = this.caelTestPuposeId;
          }
          this.checkBusinessAccount();
        }
      });
  }

  ngAfterViewInit() {
    if (this.showModal && !this.isBusinessAccount) {
      setTimeout(() => {
        this.displayModal();
      }, 0);
    }
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  displayModal() {
    this.startingModalTitle = "Important Note";
    const identityDocumentCheck = this.brandId === BrandEnum.CAEL ? 'https://www.cael.ca/take-cael/accepted-identification/' : "https://www.celpip.ca/take-celpip/test-day-information/";
    this.startingModalBody = `Before beginning your ${this.brandName} registration, ensure that you have your <a href = ${identityDocumentCheck} target="_blank"> acceptable identity document </a>
      and an accepted form of payment on hand, as they are required to complete the registration.`;
    this.startingModal?.open();
  }

  checkBusinessAccount() {
    this.businessAccountSubscription = this.store.select(selectIsBusinessAccount).pipe(take(1))
      .subscribe(
        (result) => {
          this.isBusinessAccount = result;
          if (!this.isBusinessAccount) {
            this.checkPrivateSittings();
          }
        }
      );
  }

  checkPrivateSittings() {
    if (this.brandId === BrandEnum.CELPIP && !(this.testTypeId && this.testPurposeId)) {
      if (this.affiliateId) {
        this.router.navigate(['registration/test-selection'], { queryParams: { affiliate: this.affiliateId, companyName: this.companyName } });
      } else {
        this.router.navigate(['registration/test-selection']);
      }
    }
    else {
      this.loadingService.show();
      this.privateSittingsSubscription = this.registrationService.getPrivateSittings(this.testPurposeId, this.brandId, this.testTypeId)
        .subscribe(
          result => {
            this.loadingService.hide();
            if (result.content.length > 0) {
              this.showPrivateSittings = true;
              this.showSelectSittingsTypeOption = true;
              this.privateSittings = result.content;
            }
            else {
              this.showPrivateSittings = false;
              this.showSelectSittingsTypeOption = false;
            }
          },
          () => {
            this.loadingService.hide();
            this.toastService.warning("Unable to load private sittings.");
          });
    }
  }

  finalizeSelectedSitting(sitting: TestSessionForRegistrationDto) {
    this.store.dispatch(setRegTestSession({ testSession: sitting }));
    if (!this.affiliateId && this.isBusinessAccount) {
      this.router.navigate(['batch-registration/select-accounts']);
    }
    else {
      this.router.navigate(['/registration/proof-of-identity']);
    }
  }

  toggleTestType(value: boolean) {
    this.showPrivateSittings = value;
  }

  ngOnDestroy() {
    this.privateSittingsSubscription?.unsubscribe();
    this.testInfoSubscription?.unsubscribe();
    this.businessAccountSubscription?.unsubscribe();
  }
}
